/**
 * Deployment-specific configuration.
 *
 * Values come from SvelteKit's `$env/static/public`, which Vite replaces with the
 * literal value at build time, so the built site stays fully static and needs no
 * server-side environment at runtime. SvelteKit loads these from `.env` (and
 * `.env.production`, and the real process environment), and only `PUBLIC_`
 * prefixed names are exposed.
 *
 * This module previously read `import.meta.env.PUBLIC_*`. That does not work under
 * SvelteKit: it replaces `import.meta.env` with its own object, and any key it did
 * not inject folds to `undefined` at build time. Every value here silently became
 * `undefined`, so the BlueMap origin always fell back to `localhost:8100`. The
 * namespace import below is used rather than named imports so an unset variable is
 * simply an absent property instead of an error, which keeps the variables optional
 * as documented in `.env.example`.
 *
 * Only ever read public values here. Anything secret belongs on the server and must
 * never be given a `PUBLIC_` prefix; with a static build, everything in this file
 * ends up in the browser bundle.
 */
import * as publicEnvModule from '$env/static/public';

/** Trims a value and collapses `undefined`/`""` to `undefined`. */
function optional(value: string | undefined): string | undefined {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
}

/**
 * Removes a trailing slash so URLs can be composed as `${baseUrl}/path`
 * without producing double slashes or tripping up CORS path matching.
 */
function origin(value: string | undefined): string | undefined {
	return optional(value)?.replace(/\/+$/, '');
}

/**
 * Reads a public variable by name.
 *
 * The indirection exists because `$env/static/public` is a generated module whose
 * members are only known once the values are read, and a deployment that leaves a
 * variable unset should not break the build.
 */
function read(name: string): string | undefined {
	const value = (publicEnvModule as Record<string, string | undefined>)[name];
	return typeof value === 'string' ? value : undefined;
}

export const publicEnv = {
	/**
	 * Fallback BlueMap origin, used by the server entries in `config/servers.ts`
	 * that do not hardcode a URL. Leave unset in development to run without a
	 * live BlueMap instance; maps then surface a clear "not configured" state
	 * instead of failing silently.
	 */
	bluemapBaseUrl: origin(read('PUBLIC_BLUEMAP_BASE_URL')),

	/** Optional second BlueMap instance, demonstrating the multi-server setup. */
	bluemapCreativeBaseUrl: origin(read('PUBLIC_BLUEMAP_CREATIVE_URL')),

	/**
	 * Base URL of the (future) Minecraft plugin API that will serve live players,
	 * markers, warps and shops. When unset, every API-backed feature degrades to
	 * "unavailable" rather than showing fabricated data.
	 */
	apiBaseUrl: origin(read('PUBLIC_API_BASE_URL')),

	/** Server display name, handy for per-deployment branding without code edits. */
	serverName: optional(read('PUBLIC_SERVER_NAME')),

	/** Set to `"true"` to surface configuration diagnostics in the console. */
	debug: read('PUBLIC_DEBUG') === 'true'
} as const;

export type PublicEnv = typeof publicEnv;
